import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RotateCcw, Search } from 'lucide-react';
import {
  AUDIT_ACTOR_TYPES,
  AUDIT_CATEGORIES,
  AUDIT_OUTCOMES,
  AUDIT_TARGET_TYPES,
  filterEventOptions,
  presetRange,
  toggleValue,
  type AuditFilterError,
  type AuditLogFilters,
  type AuditPeriodPreset,
} from '@entities/audit-log';
import { cn } from '@shared/lib/utils';
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  TableDateRangePicker,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';

interface ProjectOption {
  id: string;
  name: string;
}

interface AuditLogFiltersProps {
  filters: AuditLogFilters;
  errors: AuditFilterError[];
  projects: ProjectOption[];
  apiError: string | null;
  periodText: string | null;
  isRefreshing: boolean;
  onChange: (next: AuditLogFilters) => void;
  onRefresh: () => void;
  exportSlot: ReactNode;
}

const PRESETS: AuditPeriodPreset[] = ['24h', '7d', '30d', '93d'];

function AuditFilterField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <span className="mb-1 block truncate text-xs font-medium leading-none text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

const compactControlClass = 'h-9 px-3 text-sm';
const compactSelectClass = 'h-9 py-1';

const chipClassName = (active: boolean) =>
  cn(
    'inline-flex min-h-8 items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-fast ease-standard sm:min-h-0',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border text-muted-foreground hover:bg-accent'
  );

export function AuditLogFilters({
  filters,
  errors,
  projects,
  apiError,
  periodText,
  isRefreshing,
  onChange,
  onRefresh,
  exportSlot,
}: AuditLogFiltersProps) {
  const { t } = useTranslation('cabinet');
  const [moreOpen, setMoreOpen] = useState(false);
  const [eventQuery, setEventQuery] = useState('');
  const [text, setText] = useState({
    actorUserId: filters.actorUserId,
    subjectUserId: filters.subjectUserId,
    projectId: filters.projectId,
    targetId: filters.targetId,
    requestId: filters.requestId,
  });

  const textKey = [
    filters.actorUserId,
    filters.subjectUserId,
    filters.projectId,
    filters.targetId,
    filters.requestId,
  ].join('|');

  useEffect(() => {
    setText({
      actorUserId: filters.actorUserId,
      subjectUserId: filters.subjectUserId,
      projectId: filters.projectId,
      targetId: filters.targetId,
      requestId: filters.requestId,
    });
  }, [textKey, filters.actorUserId, filters.subjectUserId, filters.projectId, filters.targetId, filters.requestId]);

  useEffect(() => {
    const same =
      text.actorUserId === filters.actorUserId &&
      text.subjectUserId === filters.subjectUserId &&
      text.projectId === filters.projectId &&
      text.targetId === filters.targetId &&
      text.requestId === filters.requestId;
    if (same) return undefined;
    const timer = window.setTimeout(() => onChange({ ...filters, ...text }), 300);
    return () => window.clearTimeout(timer);
  }, [text, filters, onChange]);

  const periodInvalid = errors.some((error) =>
    ['periodOrder', 'periodTooLong', 'dateFrom', 'dateTo'].includes(error)
  );
  const eventOptions = filterEventOptions(filters.category).filter((item) =>
    item.eventType.toLowerCase().includes(eventQuery.trim().toLowerCase())
  );
  const grouped = useMemo(() => {
    return AUDIT_CATEGORIES.map((category) => ({
      category,
      items: eventOptions.filter((item) => item.category === category),
    })).filter((group) => group.items.length);
  }, [eventOptions]);

  return (
    <div className="surface-card space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <TableDateRangePicker
          compact
          className="min-w-0 max-w-full flex-1 sm:max-w-md"
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          format="iso"
          showTime
          invalid={periodInvalid}
          placeholder={t('auditLog.filters.period')}
          timeFromLabel={t('auditLog.filters.timeFrom')}
          timeToLabel={t('auditLog.filters.timeTo')}
          onChange={({ dateFrom, dateTo }) => onChange({ ...filters, dateFrom, dateTo })}
        />
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={chipClassName(false)}
            onClick={() => onChange({ ...filters, ...presetRange(preset) })}
          >
            {t(`auditLog.presets.${preset}`)}
          </button>
        ))}
      </div>

      <button
        type="button"
        className="inline-flex min-h-tap items-center gap-1 text-sm text-primary lg:hidden"
        onClick={() => setMoreOpen((open) => !open)}
      >
        {t('auditLog.filters.more')}
        <ChevronDown className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')} />
      </button>

      <div className={cn('space-y-3', !moreOpen && 'hidden', 'lg:block')}>
        <div className="flex flex-wrap gap-2">
          {AUDIT_CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              className={chipClassName(filters.category.includes(category))}
              onClick={() =>
                onChange({ ...filters, category: toggleValue(filters.category, category) })
              }
            >
              {t(`auditLog.categories.${category}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className={chipClassName(filters.eventType.length > 0)}>
                {t('auditLog.filters.eventType')}
                {filters.eventType.length ? ` · ${filters.eventType.length}` : ''}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <Input
                className={compactControlClass}
                icon={<Search className="h-4 w-4" />}
                value={eventQuery}
                placeholder={t('auditLog.filters.eventSearch')}
                onChange={(event) => setEventQuery(event.target.value)}
              />
              <div className="mt-2 max-h-64 space-y-3 overflow-y-auto">
                {grouped.map((group) => (
                  <div key={group.category}>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      {t(`auditLog.categories.${group.category}`)}
                    </p>
                    {group.items.map((item) => (
                      <label key={item.eventType} className="flex items-center gap-2 py-1 text-sm">
                        <Checkbox
                          checked={filters.eventType.includes(item.eventType)}
                          onCheckedChange={() =>
                            onChange({
                              ...filters,
                              eventType: toggleValue(filters.eventType, item.eventType),
                            })
                          }
                        />
                        <span className="min-w-0 truncate">
                          {t(`auditLog.events.${item.eventType}`, { defaultValue: item.eventType })}
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {AUDIT_OUTCOMES.map((outcome) => (
            <button
              key={outcome}
              type="button"
              className={chipClassName(filters.outcome.includes(outcome))}
              onClick={() => onChange({ ...filters, outcome: toggleValue(filters.outcome, outcome) })}
            >
              {t(`auditLog.outcomes.${outcome}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {AUDIT_ACTOR_TYPES.map((actor) => (
            <button
              key={actor}
              type="button"
              className={chipClassName(filters.actorType.includes(actor))}
              onClick={() =>
                onChange({ ...filters, actorType: toggleValue(filters.actorType, actor) })
              }
            >
              {t(`auditLog.actors.${actor}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
          <AuditFilterField
            label={t('auditLog.filters.actorUserId')}
            className="sm:col-span-1 lg:col-span-2"
          >
            <Input
              inputMode="numeric"
              className={compactControlClass}
              placeholder="ID"
              aria-label={t('auditLog.filters.actorUserId')}
              value={text.actorUserId}
              error={errors.includes('actorUserId') ? t('auditLog.errors.actorUserId') : undefined}
              onChange={(event) => setText((prev) => ({ ...prev, actorUserId: event.target.value }))}
            />
          </AuditFilterField>
          <AuditFilterField
            label={t('auditLog.filters.subjectUserId')}
            className="sm:col-span-1 lg:col-span-2"
          >
            <Input
              inputMode="numeric"
              className={compactControlClass}
              placeholder="ID"
              aria-label={t('auditLog.filters.subjectUserId')}
              value={text.subjectUserId}
              error={
                errors.includes('subjectUserId') ? t('auditLog.errors.subjectUserId') : undefined
              }
              onChange={(event) =>
                setText((prev) => ({ ...prev, subjectUserId: event.target.value }))
              }
            />
          </AuditFilterField>
          <AuditFilterField
            label={t('auditLog.filters.targetType')}
            className="sm:col-span-1 lg:col-span-3"
          >
            <Select
              value={filters.targetType || 'none'}
              onValueChange={(value) =>
                onChange({ ...filters, targetType: value === 'none' ? '' : value })
              }
            >
              <SelectTrigger className={compactSelectClass} aria-label={t('auditLog.filters.targetType')}>
                <SelectValue placeholder={t('auditLog.filters.targetType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('auditLog.filters.targetType')}</SelectItem>
                {AUDIT_TARGET_TYPES.map((target) => (
                  <SelectItem key={target} value={target}>
                    {t(`auditLog.targets.${target}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AuditFilterField>
          <AuditFilterField
            label={t('auditLog.filters.targetId')}
            className="sm:col-span-1 lg:col-span-3"
          >
            <Input
              className={compactControlClass}
              placeholder={t('auditLog.filters.targetId')}
              aria-label={t('auditLog.filters.targetId')}
              value={text.targetId}
              error={
                errors.includes('targetIdRequiresType')
                  ? t('auditLog.errors.targetIdRequiresType')
                  : errors.includes('targetId')
                    ? t('auditLog.errors.tooLong')
                    : undefined
              }
              onChange={(event) => setText((prev) => ({ ...prev, targetId: event.target.value }))}
            />
          </AuditFilterField>
          <AuditFilterField
            label={t('auditLog.filters.requestId')}
            className="sm:col-span-2 lg:col-span-2"
          >
            <Input
              className={compactControlClass}
              placeholder={t('auditLog.filters.requestId')}
              aria-label={t('auditLog.filters.requestId')}
              value={text.requestId}
              error={errors.includes('requestId') ? t('auditLog.errors.tooLong') : undefined}
              onChange={(event) => setText((prev) => ({ ...prev, requestId: event.target.value }))}
            />
          </AuditFilterField>
          <AuditFilterField
            label={t('auditLog.filters.project')}
            className="sm:col-span-2 lg:col-span-12"
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
              <Select
                value={
                  projects.some((project) => project.id === text.projectId) ? text.projectId : 'manual'
                }
                onValueChange={(value) => {
                  if (value === 'manual') return;
                  setText((prev) => ({ ...prev, projectId: value }));
                }}
              >
                <SelectTrigger className={compactSelectClass} aria-label={t('auditLog.filters.project')}>
                  <SelectValue placeholder={t('auditLog.filters.project')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t('auditLog.filters.projectManual')}</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className={compactControlClass}
                placeholder={t('auditLog.filters.projectId')}
                aria-label={t('auditLog.filters.projectId')}
                value={text.projectId}
                error={errors.includes('projectId') ? t('auditLog.errors.tooLong') : undefined}
                onChange={(event) => setText((prev) => ({ ...prev, projectId: event.target.value }))}
              />
            </div>
          </AuditFilterField>
        </div>
      </div>

      {periodInvalid ? (
        <p className="text-sm text-destructive">
          {errors.includes('periodTooLong')
            ? t('auditLog.errors.periodTooLong')
            : t('auditLog.errors.periodOrder')}
        </p>
      ) : null}
      {apiError ? <p className="text-sm text-destructive">{apiError}</p> : null}
      {periodText ? <p className="text-xs text-muted-foreground">{periodText}</p> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={onRefresh} disabled={isRefreshing}>
          <RotateCcw className="h-4 w-4" />
          {t('auditLog.refresh')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9"
          onClick={() => {
            setText({
              actorUserId: '',
              subjectUserId: '',
              projectId: '',
              targetId: '',
              requestId: '',
            });
            onChange({
              category: [],
              eventType: [],
              outcome: [],
              actorType: [],
              actorUserId: '',
              subjectUserId: '',
              projectId: '',
              targetType: '',
              targetId: '',
              requestId: '',
              dateFrom: '',
              dateTo: '',
            });
          }}
        >
          {t('auditLog.filters.reset')}
        </Button>
        <div className="ml-auto">{exportSlot}</div>
      </div>
    </div>
  );
}
